import { useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { EmailCapture } from "../components/EmailCapture";

const PARTS = [
  ["Senses", "How the world gets in"],
  ["Memory", "Her mind, saved on disk"],
  ["The Mind", "Composes every reply"],
  ["Cognitive Core", "Anticipates & prioritizes"],
  ["Voice", "Turns text into speech"],
  ["Reaching out", "She starts conversations"],
  ["Sleep & Learning", "A day becomes memory"],
  ["Acting — her hands", "She changes things, not just talks"],
];

export default function MyAI() {
  const iframeRef = useRef(null);

  const resizeIframe = useCallback(() => {
    const f = iframeRef.current;
    try {
      const doc = f?.contentWindow?.document;
      if (doc) {
        const h = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
        if (h > 0) f.style.height = h + "px";
      }
    } catch (e) {}
  }, []);

  const handleLoad = useCallback(() => {
    resizeIframe();
    try {
      const doc = iframeRef.current.contentWindow.document;
      const ro = new ResizeObserver(() => resizeIframe());
      ro.observe(doc.body);
      // re-measure after fonts/render settle
      [200, 600, 1200].forEach((d) => setTimeout(resizeIframe, d));
    } catch (e) {}
  }, [resizeIframe]);

  useEffect(() => {
    window.addEventListener("resize", resizeIframe);
    return () => window.removeEventListener("resize", resizeIframe);
  }, [resizeIframe]);

  useSeo({
    title: "My AI — Meet Mochi | Practical AI for Everyday Life",
    description: "Meet Mochi, BJ's personal AI agent. She senses, remembers, thinks, speaks, reaches out, learns while he sleeps, and takes real action. Explore the interactive map of how she thinks.",
  });

  return (
    <div>
      {/* HERO / SUMMARY */}
      <section className="bg-plexus grain relative overflow-hidden" data-testid="myai-hero">
        <div className="plexus-edge-corner absolute inset-0 opacity-30" />
        <div className="container-brand relative py-20 md:py-24">
          <p className="eyebrow !text-ochre-bright">My AI</p>
          <h1 className="mt-4 max-w-3xl text-5xl leading-[1] text-bone sm:text-6xl">
            Meet Mochi — <span className="italic text-ochre-bright">built in the open.</span>
          </h1>
          <p className="mt-6 max-w-measure text-lg text-on-navy-body">
            Mochi is my personal AI — but not the kind you just chat with. She's built like a living system.
            She takes the world in, keeps a real memory, thinks and composes every reply, speaks out loud,
            reaches out to me on her own, sleeps and turns each day into long-term memory, knows herself, and
            can actually <span className="italic text-bone">take action</span> — not just talk.
          </p>
          <div className="mt-8 grid max-w-3xl grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            {PARTS.map(([t, d]) => (
              <div key={t} className="border-l-2 border-ochre/50 pl-3">
                <p className="font-sans text-[12px] font-bold uppercase tracking-[0.1em] text-bone">{t}</p>
                <p className="text-[12px] leading-snug text-on-navy-muted">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO — what she's like to talk to */}
      <section className="bg-warm grain border-y border-navy/10" data-testid="myai-video-section">
        <div className="container-brand grid items-center gap-10 py-16 md:grid-cols-2">
          <div>
            <p className="eyebrow">In her own voice</p>
            <h2 className="mt-3 text-3xl leading-tight md:text-4xl">What she's like <span className="italic text-ochre">when we talk.</span></h2>
            <p className="mt-4 max-w-md text-text-body">
              A quick, unedited peek at a real conversation with Mochi — how she listens, thinks, and responds
              in the moment.
            </p>
          </div>
          <div className="overflow-hidden rounded-card border border-navy/15 bg-navy shadow-hero">
            <video controls playsInline preload="none" poster="/brand/mochi-listening.webp" className="w-full" data-testid="myai-video">
              <source src="/mochi-talk.mp4" type="video/mp4" />
              Your browser does not support embedded video.
            </video>
          </div>
        </div>
      </section>

      {/* INTERACTIVE MAP */}
      <section className="sheet grain" data-testid="myai-map-section">
        <div className="mx-auto max-w-[1600px] px-6 py-16 md:px-10">
          <div className="mb-8">
            <p className="eyebrow">Inside her head</p>
            <h2 className="mt-3 text-3xl leading-[1.05] md:text-4xl">How Mochi <span className="italic text-ochre">actually thinks.</span></h2>
            <p className="mt-4 max-w-measure text-text-body">
              This is the real architecture of her brain. Tap any part to explore it, or trace a loop to watch a
              single thought travel start to finish.
            </p>
          </div>
          <div className="overflow-hidden rounded-card border border-navy/15 shadow-hero">
            <iframe
              ref={iframeRef}
              src="/mochi-brain.html"
              title="Mochi's Brain — interactive map"
              onLoad={handleLoad}
              scrolling="no"
              className="block w-full"
              style={{ height: 1200, border: "0" }}
              data-testid="myai-map-iframe"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-plexus-clear grain">
        <div className="container-brand max-w-3xl py-16">
          <EmailCapture source="my-ai" variant="navy" heading="Follow Mochi's build" sub="I share what she can do, what breaks, and what's next — in plain English." redirectToThankYou />
          <Link to="/setups" className="on-navy mt-6 inline-flex items-center gap-2 font-sans text-[13px] font-bold uppercase tracking-[0.12em] text-ochre-bright no-underline">
            Explore the free setups <ArrowRight size={15} />
          </Link>
        </div>
      </section>
    </div>
  );
}
